import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import stateCityData from "../../data/states.json";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCity,
  faEnvelope,
  faMapMarkerAlt,
  faPhoneAlt,
  faUser,
  faNewspaper,
  faVenusMars,
  faIdCard,
  faTint,
  faEye,
  faEyeSlash,
  faUserPlus,
  faArrowRight,
  faArrowLeft,
  faCheckCircle,
  faCalendar,
  faBullhorn,
  faMicrophone,
  faCamera,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { toast } from "react-toastify";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get role from URL if present
  const searchParams = new URLSearchParams(location.search);
  const roleFromUrl = searchParams.get("role");
  const [selectedRole, setSelectedRole] = useState(roleFromUrl || "");

  // Add a new state to track registration step
  const [registrationStep, setRegistrationStep] = useState(1);

  // Add function to go back to role selection
  const goBackToRoleSelection = () => {
    setRegistrationStep(1);
  };

  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    email: "",
    mobile: "",
    aadharNo: "",
    pancard: "",
    dateOfBirth: "",
    bloodType: "",
    residenceaddress: "",
    state: "",
    city: "",
    pincode: "",
    password: "",
    confirmpassword: "",
  });

  const [states] = useState(Object.keys(stateCityData));
  const [cities, setCities] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtpFields, setShowOtpFields] = useState(false);
  const [timer, setTimer] = useState(120);
  // Add timer countdown using setTimeout
  React.useEffect(() => {
    if (!showOtpFields) return;
    if (timer > 0) {
      const timeout = setTimeout(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearTimeout(timeout);
    } else if (timer === 0) {
      // Enable resend button when timer reaches 0
      setCanResendOTP(true);
    }
  }, [timer, showOtpFields]);
  const [isResending, setIsResending] = useState(false);
  const [canResendOTP, setCanResendOTP] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [otpData, setOtpData] = useState({
    otpEmail: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Mobile: allow free typing (no cutting); validate on submit
    if (name === "mobile") {
      setFormData({
        ...formData,
        [name]: value,
      });
    } else if (name === "aadharNo") {
      // Only allow digits, max 12
      const digitsOnly = value.replace(/\D/g, "").slice(0, 12);
      setFormData({
        ...formData,
        [name]: digitsOnly,
      });
    } else if (name === "name") {
      // Only allow letters and spaces
      const lettersOnly = value.replace(/[^A-Za-z\s]/g, "");
      setFormData({
        ...formData,
        [name]: lettersOnly,
      });
    } else if (name === "pancard") {
      // Allow any characters, only limit length to 10
      const pancardValue = value.slice(0, 10);
      setFormData({
        ...formData,
        [name]: pancardValue,
      });
    } else if (name === "pincode") {
      // Only allow digits, max 6
      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
      setFormData({
        ...formData,
        [name]: digitsOnly,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    setFormError("");
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData({
      ...formData,
      state: selectedState,
      city: "",
    });

    // Get cities for selected state from the imported data
    if (selectedState) {
      setCities(stateCityData[selectedState] || []);
    } else {
      setCities([]);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setRegistrationStep(2); // Move to form step after role selection
  };

  const validateForm = () => {
    // Check all input field validations first
    // Check if required fields are filled
    // List of required fields and their labels
    const requiredFields = [
      { key: "name", label: "Full Name" },
      { key: "gender", label: "Gender" },
      { key: "email", label: "Email" },
      { key: "mobile", label: "Mobile Number" },
      { key: "aadharNo", label: "Aadhar Number" },
      { key: "dateOfBirth", label: "Date of Birth" },
      { key: "pancard", label: "PAN Card" },
      { key: "residenceaddress", label: "Residence Address" },
      { key: "bloodType", label: "Blood Type" },
      { key: "state", label: "State" },
      { key: "city", label: "City" },
      { key: "pincode", label: "PinCode" },
      { key: "password", label: "Password" },
      { key: "confirmpassword", label: "Confirm Password" },
      { key: "termsAccepted", label: "Terms and Conditions" },
    ];
    const missingFields = requiredFields.filter((f) => !formData[f.key]);
    if (missingFields.length > 0) {
      const missingLabels = missingFields.map((f) => f.label).join(", ");
      const errorMsg = `Please fill the following field${missingFields.length > 1 ? "s" : ""
        }: ${missingLabels}`;
      setFormError(errorMsg);
      if (typeof toast === "function") {
        toast.error(errorMsg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    // Validate name (must contain only letters and spaces, no numbers)
    const nameRegex = /^[A-Za-z\s]+$/;
    if (!nameRegex.test(formData.name)) {
      const msg =
        "Name must contain only letters and spaces (no numbers allowed)";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    // Check if email is missing
    if (!formData.email) {
      const msg = "Email is required.";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      const msg = "Please enter a valid email address";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    // Only allow emails ending with @gmail.com
    if (!formData.email.endsWith("@gmail.com")) {
      const msg = "Only @gmail.com email addresses are allowed";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    // Validate mobile number (must be exactly 10 digits, no alphabets or extra digits)
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(formData.mobile)) {
      let msg = "";
      if (formData.mobile.length < 10) {
        msg = "Mobile number must be exactly 10 digits.";
      } else {
        msg = `Mobile number must be exactly 10 digits and contain only numbers. You entered ${formData.mobile.replace(/\D/g, "").length
          } digits.`;
      }
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    // Validate Aadhar number (must be filled, exactly 12 digits, only numbers, no alphabets, not more or less)
    if (!formData.aadharNo) {
      const msg = "Aadhar number is required.";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    const aadharRegex = /^\d{12}$/;
    if (!aadharRegex.test(formData.aadharNo)) {
      let msg = "";
      if (formData.aadharNo.length !== 12) {
        msg = "Aadhar number must be exactly 12 digits.";
      } else {
        msg =
          "Aadhar number must be exactly 12 digits and contain only numbers (no alphabets or extra digits).";
      }
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    // PAN card: only enforce exactly 10 characters, any format
    if (!formData.pancard || formData.pancard.length !== 10) {
      const msg = "PAN card must be exactly 10 characters";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    // Validate date of birth (must be filled, in the past, and above 13 years)
    if (!formData.dateOfBirth) {
      const msg = "Date of birth is required.";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    const today = new Date();
    const dob = new Date(formData.dateOfBirth);
    if (dob >= today) {
      const msg = "Date of birth must be in the past";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    // User must be strictly above 13 years old (not just 13)
    const minAge = 13;
    const yearDiff = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    const dayDiff = today.getDate() - dob.getDate();
    let isAbove13 = false;
    if (yearDiff > minAge) {
      isAbove13 = true;
    } else if (yearDiff === minAge) {
      if (monthDiff > 0) {
        isAbove13 = true;
      } else if (monthDiff === 0 && dayDiff > 0) {
        isAbove13 = true;
      }
    }
    if (!isAbove13) {
      const msg = "You must be above 13 years old to register";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    // validate pincode (must be exactly 6 digits)
    const pincodeRegex = /^[0-9]{6}$/;
    if (!pincodeRegex.test(formData.pincode)) {
      let msg = "";
      if (formData.pincode.length < 6) {
        msg = "Pincode must be exactly 6 digits.";
      } else {
        msg =
          "Pincode must be exactly 6 digits and contain only numbers (no alphabets or extra digits).";
      }
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    // Validate password (at least 8 characters)
    if (formData.password.length < 8) {
      const msg = "Password must be at least 8 characters long";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    // Check if passwords match
    if (formData.password !== formData.confirmpassword) {
      const msg = "Passwords do not match";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    // LAST: check terms and conditions
    if (!formData.termsAccepted) {
      const msg = "You must accept the terms and conditions";
      setFormError(msg);
      if (typeof toast === "function") {
        toast.error(msg);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");

    // Validate form before submission
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      console.log("Sending registration data:", formData); // Debug log

      // Send form data to backend API
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/register`,
        {
          ...formData,
          role: selectedRole,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Registration response:", response); // Debug log

      // Always show OTP fields after successful form submission
      // setShowOtpFields(true);

      if (response.status === 400) {
        toast.error("User already exists. Please login.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }

      if (response.status === 200) {
        setShowOtpFields(true);
        setTimer(120); // Reset timer
        setCanResendOTP(false); // Disable resend button initially
      }

      if (!response.data.success) {
        setFormError(
          response.data.message || "Registration failed. Please try again."
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      setFormError(
        error.response?.data?.message ||
        "Registration failed. Please try again."
      );
      if (error.response && error.response.status === 400) {
        //Show some error message
        toast.error(
          error.response.data.message ||
          "Registration failed. Please try again."
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (e) => {
    setOtpData({
      ...otpData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOtpSubmit = async (e) => {
    console.log("🔍 Frontend OTP Submit Debug - Starting OTP verification");
    e.preventDefault();

    const requestData = {
      email: formData.email,
      mobile: formData.mobile,
      otpEmail: otpData.otpEmail,
    };

    console.log("🔍 Frontend OTP Submit Debug - Request data:", requestData);
    console.log("🔍 Frontend OTP Submit Debug - OTP types:", {
      otpEmail: typeof otpData.otpEmail,
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/verify-otp`,
        requestData
      );

      console.log("🔍 Frontend OTP Submit Debug - Response:", response);
      if (response.status === 200) {
        toast.success("Registration successful! Redirecting to login...");
        setShowOtpFields(false);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setFormError(response.data.message || "OTP verification failed");
      }
    } catch (error) {
      console.error("🔍 Frontend OTP Submit Debug - Error:", error);
      console.error("🔍 Frontend OTP Submit Debug - Error response:", error.response?.data);

      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.msg || "Invalid or expired OTP";
        toast.error(errorMessage);
        setFormError(errorMessage);
      } else {
        const errorMessage = error.response?.data?.message || "OTP verification failed";
        toast.error(errorMessage);
        setFormError(errorMessage);
      }
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/resend/otp`,
        {
          email: formData.email,
          mobile: formData.mobile,
        }
      );

      if (response.data.success) {
        // Reset timer and disable resend button
        setTimer(120);
        setCanResendOTP(false);
        toast.success("OTP resent successfully!");
      } else {
        toast.error(response.data.message || "Failed to resend OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-4 px-4 sm:px-6 lg:px-8 pt-20">
      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        .animate-heartbeat {
          animation: heartbeat 2s ease-in-out infinite;
        }
      `}</style>
      <div className="w-full max-w-6xl mx-auto bg-white rounded-[2rem] shadow-2xl shadow-indigo-200/50 overflow-hidden border border-slate-100 min-h-[700px]">
        <div className="flex flex-col lg:flex-row h-full">

          {/* Left side - Company Branding */}
          <div className="hidden lg:flex lg:w-5/12 bg-gradient-to-br from-indigo-700 via-blue-600 to-indigo-900 p-10 relative overflow-hidden flex-col justify-center items-center">
            {/* Abstract Background Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl -mr-20 -mt-20"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-400 opacity-20 rounded-full blur-2xl -ml-10 -mb-10"></div>

            <div className="relative z-10 flex flex-col justify-center items-center text-white">
              <div className="w-20 h-20 mb-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform duration-500 hover:scale-105">
                <img
                  src="/images/iinsaf.png"
                  alt="IINSAF Logo"
                  className="w-14 h-auto filter brightness-110 drop-shadow-md"
                />
              </div>

              <div className="text-center flex flex-col items-center">
                <h1 className="text-4xl font-[1000] tracking-tight mb-4 drop-shadow-sm uppercase">Join IINSAF</h1>
                <p className="text-indigo-100 font-bold tracking-wide text-sm opacity-90 max-w-xs mx-auto leading-relaxed mb-4">
                  Become a part of the fastest growing media & advertising network. Start your journey today!
                </p>
              </div>
            </div>
            {/* Embedded YouTube video directly */}
            <div className="relative z-10 mt-6 w-full max-w-[240px] aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20 transition-transform duration-700 hover:-translate-y-1 animate-heartbeat">
              <iframe
                src="https://www.youtube.com/embed/QcjPmHl-WTE"
                title="How to Register"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>

          {/* Right side - Form Navigation & Inputs */}
          <div className="w-full lg:w-7/12 p-6 sm:p-10 lg:p-12 flex flex-col bg-white justify-center h-full">
            {registrationStep === 1 ? (
              // Step 1: Role selection
              <div className="w-full max-w-md mx-auto py-4 animate-fadeIn">
                <div className="text-center lg:text-left mb-8">
                  <h1 className="text-3xl sm:text-4xl font-[1000] text-slate-900 tracking-tight mb-2 uppercase">
                    Select Role
                  </h1>
                  <div className="w-12 h-1.5 bg-indigo-600 rounded-full mb-4 mx-auto lg:mx-0"></div>
                  <p className="text-sm font-bold text-slate-500 tracking-wide">
                    Choose an account type to get started
                  </p>
                </div>

                {/* Watch registration guide banner (mobile only) */}
                <div className="flex lg:hidden flex-col items-center p-4 bg-rose-50 border border-rose-100 rounded-2xl mb-6 shadow-inner w-full">
                  <span className="text-xs font-black uppercase tracking-widest text-rose-700 mb-2">Video Guide: How to Register</span>
                  <div className="w-full max-w-[200px] aspect-[9/16] rounded-2xl overflow-hidden shadow-md border border-rose-100 animate-heartbeat">
                    <iframe
                      src="https://www.youtube.com/embed/QcjPmHl-WTE"
                      title="How to Register"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                </div>

                <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100 mb-6">
                  <div className="flex items-center text-indigo-800">
                    <div className="w-8 h-8 rounded-full bg-indigo-200/50 flex items-center justify-center mr-3 shadow-inner">
                      <FontAwesomeIcon icon={faUserPlus} className="text-indigo-600 font-bold" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-widest text-indigo-700">Action Required: Select a Role</span>
                  </div>
                </div>

                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1 mb-3">
                  Available Roles
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    type="button"
                    onClick={() => handleRoleSelect("Reporter")}
                    className="flex items-center justify-start p-4 bg-slate-50 border-2 border-slate-100 focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 rounded-2xl transition-all hover:bg-white hover:border-indigo-500 hover:shadow-lg hover:shadow-indigo-100/50 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                      <FontAwesomeIcon icon={faNewspaper} className="text-blue-600 text-lg" />
                    </div>
                    <div className="text-left">
                      <span className="block text-sm font-black text-slate-800 uppercase tracking-wide">Reporter</span>
                      <p className="text-xs font-bold text-slate-500 mt-0.5">Report news and events</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRoleSelect("Influencer")}
                    className="flex items-center justify-start p-4 bg-slate-50 border-2 border-slate-100 focus:ring-4 focus:ring-purple-50 focus:border-purple-500 rounded-2xl transition-all hover:bg-white hover:border-purple-500 hover:shadow-lg hover:shadow-purple-100/50 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                      <FontAwesomeIcon icon={faUser} className="text-purple-600 text-lg" />
                    </div>
                    <div className="text-left">
                      <span className="block text-sm font-black text-slate-800 uppercase tracking-wide">Influencer</span>
                      <p className="text-xs font-bold text-slate-500 mt-0.5">Create and share content</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRoleSelect("Advertiser")}
                    className="flex items-center justify-start p-4 bg-slate-50 border-2 border-slate-100 focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 rounded-2xl transition-all hover:bg-white hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-100/50 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                      <FontAwesomeIcon icon={faUser} className="text-emerald-600 text-lg" />
                    </div>
                    <div className="text-left">
                      <span className="block text-sm font-black text-slate-800 uppercase tracking-wide">Advertiser</span>
                      <p className="text-xs font-bold text-slate-500 mt-0.5">Promote products and services</p>
                    </div>
                  </button>
                </div>
              </div>
            ) : (
              // Step 2: Registration form
              <div className="w-full mx-auto animate-fadeIn">
                <div className="text-center lg:text-left mb-6">
                  <h1 className="text-2xl sm:text-3xl font-[1000] text-slate-900 tracking-tight mb-2 uppercase">
                    Register
                  </h1>
                  <div className="w-12 h-1.5 bg-indigo-600 rounded-full mb-3 mx-auto lg:mx-0"></div>
                  <p className="text-xs font-bold text-slate-500 tracking-wide">
                    Complete your profile to get started
                  </p>
                </div>

                {/* Compact watch registration guide button (mobile only) */}
                <div className="mb-4 text-center lg:text-left block lg:hidden">
                  <a
                    href="https://youtube.com/shorts/QcjPmHl-WTE?si=U4fX5X_cvLfDDrvA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100/70 border border-rose-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-rose-700 transition-all duration-300 hover:shadow-md hover:shadow-rose-100/20 group animate-heartbeat"
                  >
                    <FontAwesomeIcon icon={faPlay} className="text-rose-600 animate-pulse text-[9px]" />
                    <span>How to Register? Watch Guide</span>
                  </a>
                </div>

                <div className="mb-6 flex flex-col sm:flex-row items-center justify-between bg-slate-50 p-3 rounded-xl border border-slate-100 gap-3 sm:gap-0">
                  <span className="inline-flex items-center px-4 py-1.5 rounded-xl text-[11px] font-black uppercase tracking-widest bg-white border-2 border-indigo-100 text-indigo-700 shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 mr-2 animate-pulse"></span>
                    Role: {selectedRole}
                  </span>
                  <button
                    onClick={goBackToRoleSelection}
                    className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-800 flex items-center bg-indigo-50/50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    ← Change Role
                  </button>
                </div>

                {/* Role selection - compact version */}
                <div className="mb-6 hidden">
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Change Your Role
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => handleRoleSelect("Reporter")}
                      className={`flex flex-col items-center justify-center p-2 border rounded-md transition-all ${selectedRole === "Reporter"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200 hover:border-blue-300 text-gray-600"
                        }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mb-1">
                        <FontAwesomeIcon
                          icon={faNewspaper}
                          className="text-blue-600 text-xs"
                        />
                      </div>
                      <span className="text-xs font-medium">Reporter</span>
                      {selectedRole === "Reporter" && (
                        <span className="mt-1 inline-block px-1 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          ✓
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleRoleSelect("Influencer")}
                      className={`flex flex-col items-center justify-center p-2 border rounded-md transition-all ${selectedRole === "Influencer"
                        ? "border-purple-500 bg-purple-50 text-purple-700"
                        : "border-gray-200 hover:border-purple-300 text-gray-600"
                        }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mb-1">
                        <FontAwesomeIcon
                          icon={faUser}
                          className="text-purple-600 text-xs"
                        />
                      </div>
                      <span className="text-xs font-medium">Influencer</span>
                      {selectedRole === "Influencer" && (
                        <span className="mt-1 inline-block px-1 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                          ✓
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleRoleSelect("Advertiser")}
                      className={`flex flex-col items-center justify-center p-2 border rounded-md transition-all ${selectedRole === "Advertiser"
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-gray-200 hover:border-green-300 text-gray-600"
                        }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mb-1">
                        <FontAwesomeIcon
                          icon={faUser}
                          className="text-green-600 text-xs"
                        />
                      </div>
                      <span className="text-xs font-medium">Advertiser</span>
                      {selectedRole === "Advertiser" && (
                        <span className="mt-1 inline-block px-1 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          ✓
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Registration form */}
                <form onSubmit={handleSubmit} className="space-y-3">
                  {formError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium">
                      {formError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* Name Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Full Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faUser}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                          placeholder="Full Name"
                          required
                        />
                      </div>
                    </div>

                    {/* Gender Field */}
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1 mb-1">
                        Gender
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faVenusMars}
                            className="text-slate-400 group-focus-within:text-indigo-500 transition-colors pointer-events-none"
                          />
                        </div>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          className="block w-full pl-10 pr-4 py-2 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 appearance-none"
                          required
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Email Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                          placeholder="Email"
                          required
                        />
                      </div>
                    </div>

                    {/* Mobile Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Mobile Number
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faPhoneAlt}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type="tel"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                          placeholder="Mobile"
                          required
                        />
                      </div>
                    </div>

                    {/* Aadhar Card field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Aadhar Number
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faIdCard}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type="text"
                          name="aadharNo"
                          value={formData.aadharNo}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                          placeholder="Aadhar No."
                          pattern="[0-9]{12}"
                          maxLength="12"
                        />
                      </div>
                    </div>

                    {/* PAN Card field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        PAN Card
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faIdCard}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type="text"
                          name="pancard"
                          value={formData.pancard}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                          placeholder="PAN Card"
                        />
                      </div>
                    </div>

                    {/* Date of Birth field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Date of Birth
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        />
                      </div>
                    </div>

                    {/* Blood Type field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Blood Type
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faTint}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <select
                          name="bloodType"
                          value={formData.bloodType}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm appearance-none"
                        >
                          <option value="">Select Blood Type</option>
                          <option value="A+">A+</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B-">B-</option>
                          <option value="AB+">AB+</option>
                          <option value="AB-">AB-</option>
                          <option value="O+">O+</option>
                          <option value="O-">O-</option>
                        </select>
                      </div>
                    </div>

                    {/* State Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        State
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <select
                          name="state"
                          value={formData.state}
                          onChange={handleStateChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm appearance-none"
                          required
                        >
                          <option value="">Select State</option>
                          {states.map((state, index) => (
                            <option key={index} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* City Field */}
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1 mb-1">
                        City
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faCity}
                            className="text-slate-400 group-focus-within:text-indigo-500 transition-colors pointer-events-none"
                          />
                        </div>
                        <select
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="block w-full pl-10 pr-4 py-2 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 appearance-none disabled:opacity-50"
                          required
                          disabled={!formData.state}
                        >
                          <option value="">Select City</option>
                          {cities.map((city, index) => (
                            <option key={index} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Address Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Residence Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type="text"
                          name="residenceaddress"
                          value={formData.residenceaddress}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                          placeholder="Address"
                        />
                      </div>
                    </div>

                    {/* PinCode Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        PinCode
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                          placeholder="PinCode"
                          required
                        />
                      </div>
                    </div>

                    {/* Password Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faEye}
                            className="text-gray-400 text-xs"
                          />
                        </div>
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          className="block w-full pl-8 pr-8 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                          placeholder="Password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-2 flex items-center"
                        >
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            className="text-gray-400 hover:text-gray-600 text-xs"
                          />
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1 mb-1">
                        Confirm Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <FontAwesomeIcon
                            icon={faEye}
                            className="text-slate-400 group-focus-within:text-indigo-500 transition-colors pointer-events-none"
                          />
                        </div>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmpassword"
                          value={formData.confirmpassword}
                          onChange={handleChange}
                          className="block w-full pl-10 pr-10 py-2 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900"
                          placeholder="Confirm Password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute inset-y-0 right-3 flex items-center text-slate-400 group-focus-within:text-indigo-500 hover:text-indigo-600 transition-colors"
                        >
                          <FontAwesomeIcon
                            icon={showConfirmPassword ? faEyeSlash : faEye}
                            className="text-sm"
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Terms and conditions */}
                  <div className="flex items-start mt-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <div className="flex items-center h-5">
                      <input
                        id="terms"
                        name="termsAccepted"
                        type="checkbox"
                        checked={!!formData.termsAccepted}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            termsAccepted: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-indigo-600 bg-white border-2 border-slate-300 rounded focus:ring-indigo-500"
                      />
                    </div>
                    <div className="ml-3 text-xs font-bold text-slate-600 leading-relaxed pt-[2px]">
                      I accept the <span
                        className="text-indigo-600 hover:text-indigo-700 underline cursor-pointer"
                        onClick={() => window.open('/termscondition', '_blank')}
                      >
                        terms and conditions
                      </span> of IINSAF.
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-8 pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-lg shadow-indigo-200/50 text-sm font-black text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 active:scale-[0.98] transition-all uppercase tracking-widest ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          PROCESSING ACCOUNT...
                        </>
                      ) : (
                        <>
                          CREATE ACCOUNT{" "}
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className="ml-2 text-sm"
                          />
                        </>
                      )}
                    </button>
                  </div>

                  <div className="text-center mt-3">
                    <span className="text-xs text-gray-600">
                      Already have an account?
                    </span>
                    <Link
                      to="/login"
                      className="inline-flex items-center text-blue-600 font-medium ml-2 hover:underline text-xs"
                    >
                      Sign in{" "}
                      <FontAwesomeIcon icon={faArrowRight} className="ml-1 text-xs" />
                    </Link>
                  </div>
                </form>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-slate-100">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-4">Quick Registration Links</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link
                  to="/press/register"
                  className="flex items-center justify-center p-3 bg-blue-50/50 hover:bg-blue-50 border border-blue-100/50 rounded-xl transition-colors"
                >
                  <FontAwesomeIcon icon={faCamera} className="text-blue-500 text-sm mr-2" />
                  <span className="text-[11px] font-bold text-blue-700">Press</span>
                </Link>

                <Link
                  to="/raise/register"
                  className="flex items-center justify-center p-3 bg-emerald-50/50 hover:bg-emerald-50 border border-emerald-100/50 rounded-xl transition-colors"
                >
                  <FontAwesomeIcon icon={faBullhorn} className="text-emerald-500 text-sm mr-2" />
                  <span className="text-[11px] font-bold text-emerald-700">Raise Voice</span>
                </Link>

                <Link
                  to="/podcast/register"
                  className="flex items-center justify-center p-3 bg-purple-50/50 hover:bg-purple-50 border border-purple-100/50 rounded-xl transition-colors"
                >
                  <FontAwesomeIcon icon={faMicrophone} className="text-purple-500 text-sm mr-2" />
                  <span className="text-[11px] font-bold text-purple-700">Podcast</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal */}
      {showOtpFields && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"></div>
          <div className="bg-white w-full max-w-md mx-auto p-8 rounded-[2rem] shadow-2xl shadow-indigo-500/20 relative animate-fadeIn border border-slate-100">
            <button
              onClick={() => setShowOtpFields(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
              aria-label="Close OTP Modal"
            >
              <span className="text-xl leading-none font-bold">&times;</span>
            </button>
            <div className="flex flex-col items-center justify-center mb-6">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h3 className="text-2xl font-[1000] text-slate-800 uppercase tracking-tight mb-1">
                Verify OTP
              </h3>
              <div className="text-[10px] uppercase tracking-widest font-black text-rose-500 bg-rose-50 px-3 py-1 rounded-full mt-2 border border-rose-100 shadow-sm">
                Time remaining: {formatTime(timer)}
              </div>
            </div>

            {successMessage && (
              <div className="mb-4 p-3 bg-emerald-50 text-emerald-700 font-bold text-xs rounded-xl text-center border border-emerald-100">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                  Email OTP
                </label>
                <input
                  type="text"
                  id="otpEmail"
                  name="otpEmail"
                  maxLength={6}
                  value={otpData.otpEmail}
                  onChange={handleOtpChange}
                  className="block w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 tracking-[0.2em] text-center"
                  placeholder="------"
                />
                <p className="mt-1 text-[10px] font-bold text-slate-400 text-center">
                  Check your email for the OTP
                </p>
              </div>



              <div className="flex flex-col gap-3 mt-6 pt-2">
                <button
                  type="submit"
                  className="w-full flex justify-center py-3.5 px-4 rounded-xl shadow-lg shadow-emerald-200/50 text-sm font-black text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 active:scale-[0.98] transition-all uppercase tracking-widest"
                >
                  Verify OTP
                </button>

                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={!canResendOTP || isResending}
                  className={`w-full flex justify-center py-3.5 px-4 rounded-xl text-sm font-black transition-all uppercase tracking-widest ${canResendOTP && !isResending
                    ? "text-indigo-600 bg-indigo-50 hover:bg-indigo-100 active:scale-[0.98] border-2 border-indigo-100 hover:border-indigo-200"
                    : "text-slate-400 bg-slate-50 border-2 border-slate-100 cursor-not-allowed"
                    }`}
                >
                  {isResending ? "Resending..." : "Resend OTP"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )
      }

      {/* Toast Notification */}
      {
        showToast && (
          <div className="fixed top-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md z-50">
            <div className="flex">
              <div className="py-1">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-green-500 mr-2"
                />
              </div>
              <div>
                <p className="font-bold">Success</p>
                <p className="text-sm">{toastMessage}</p>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default Register;
